/**
 * JoinOnColumnForm — the "pick the ON columns" step for joining a table that
 * has no foreign-key edge to the FROM-side tables. Renders
 * `‹in-scope›.‹col› = ‹new›.‹col›` as two column dropdowns plus Add / back.
 *
 * The LEFT side spans EVERY already-in-scope table (grouped into <optgroup>s by
 * table/alias) so the user can join the new table to any existing one — not
 * just the leading FROM item. Its option `value` is the column's SQL reference
 * (`alias.column`, dialect-quoted — the picker item's `sqlRef`), which becomes
 * the left side of the emitted ON predicate verbatim. The RIGHT side is the table being joined. The caller builds and
 * commits the `AddJoinClause`; the user can still refine the predicate via the
 * inline ON pill afterward.
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove JOIN.
 */

import { useState } from 'react';
import { ChevronLeft } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import type { Column } from '../../../../sql-resolver/types/schema';
import { columnGroupTitle, type ColumnGroup } from '../common/columnGroups';

export interface JoinOnColumnFormProps {
  readonly testId: string;
  /** Display label for the table being joined — its derived alias. */
  readonly rightLabel: string;
  /** All in-scope tables' columns, grouped by table/alias (the left options). */
  readonly leftGroups: readonly ColumnGroup[];
  /** Columns of the table being joined (the right options). */
  readonly rightColumns: readonly Column[];
  /** The SQL reference (`sqlRef`) to pre-select on the left. */
  readonly defaultLeftQualified?: string;
  readonly defaultRightColumn?: string;
  readonly onBack: () => void;
  readonly onSubmit: (args: { leftQualified: string; rightColumn: string }) => void;
}

export function JoinOnColumnForm({
  testId,
  rightLabel,
  leftGroups,
  rightColumns,
  defaultLeftQualified,
  defaultRightColumn,
  onBack,
  onSubmit,
}: JoinOnColumnFormProps) {
  const [leftQualified, setLeftQualified] = useState(defaultLeftQualified ?? '');
  const [rightColumn, setRightColumn] = useState(defaultRightColumn ?? '');
  const canAdd = leftQualified.length > 0 && rightColumn.length > 0;

  return (
    <form
      className="flex flex-col gap-2"
      data-testid={`${testId}-form`}
      onSubmit={(e) => {
        e.preventDefault();
        if (canAdd) onSubmit({ leftQualified, rightColumn });
      }}
    >
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="-ml-1 flex items-center rounded p-0.5 text-muted-foreground transition-transform hover:bg-muted active:scale-[0.96]"
          data-testid={`${testId}-back`}
          onClick={onBack}
          aria-label="Back to table list"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <span className="font-mono text-[11px] text-foreground" data-testid={`${testId}-heading`}>
          Join <strong>{rightLabel}</strong> on
        </span>
      </div>
      <div className="flex items-end gap-1.5">
        <label className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate text-muted-foreground">From</span>
          <select
            data-testid={`${testId}-left`}
            className="rounded border border-input bg-background px-2 py-1"
            value={leftQualified}
            onChange={(e) => setLeftQualified(e.target.value)}
          >
            <option value="" disabled>Select column…</option>
            {leftGroups.length === 0 ? (
              <option value="">— no columns —</option>
            ) : (
              leftGroups.map((group, gi) => (
                <optgroup key={group.relationKey || gi} label={columnGroupTitle(group)}>
                  {group.items.map((it) => (
                    <option key={it.key} value={it.sqlRef}>
                      {it.columnName}
                    </option>
                  ))}
                </optgroup>
              ))
            )}
          </select>
        </label>
        <span className="pb-1.5 font-mono text-foreground">=</span>
        <label className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate text-muted-foreground">{rightLabel}</span>
          <select
            data-testid={`${testId}-right`}
            className="rounded border border-input bg-background px-2 py-1"
            value={rightColumn}
            onChange={(e) => setRightColumn(e.target.value)}
          >
            <option value="" disabled>Select column…</option>
            {rightColumns.length === 0 ? (
              <option value="">— no columns —</option>
            ) : (
              rightColumns.map((c, i) => (
                <option key={`${c.qualified.name}::${i}`} value={c.qualified.name}>
                  {c.qualified.name}
                </option>
              ))
            )}
          </select>
        </label>
      </div>
      <Button
        type="submit"
        size="sm"
        disabled={!canAdd}
        aria-disabled={!canAdd}
        data-testid={`${testId}-apply`}
        className="self-end transition-transform active:scale-[0.96]"
      >
        Add join
      </Button>
    </form>
  );
}
